import React, { useState, useEffect } from 'react';
import { 
  Card, Table, Button, Space, message, Popconfirm, Tag, Tabs 
} from 'antd';
import { 
  PlusOutlined, DeleteOutlined, EyeOutlined, SyncOutlined, CheckCircleOutlined, CloseCircleOutlined 
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { matchApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './style.css';

const MatchList = () => {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [activeTab, setActiveTab] = useState('题答分离'); // 修改初始值为中文
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const fetchRecords = async () => {
    setLoading(true);
    try {
      console.log('开始获取匹配记录，匹配类型:', activeTab);
      const response = await matchApi.getMatchList({ match_type: activeTab });
      console.log('获取匹配记录响应:', response);
      
      // 处理直接返回的数组格式
      if (Array.isArray(response)) {
        setRecords(response);
      } else if (response && response.code === 0 && Array.isArray(response.data)) {
        // 兼容处理标准响应格式
        setRecords(response.data);
      } else {
        setRecords([]);
        console.error('响应数据格式不正确:', response);
      }
    } catch (error) {
      console.error('获取匹配记录失败，详细错误:', error);
      if (error.response) {
        console.error('服务器响应:', error.response.data);
        console.error('状态码:', error.response.status);
      }
      message.error('获取匹配记录失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, [activeTab]);

  const handleDelete = async (id) => {
    try {
      await matchApi.deleteRecord(id);
      message.success('删除成功');
      fetchRecords();
    } catch (error) {
      message.error('删除失败');
      console.error(error);
    }
  };

  const handleStartMatch = async (id) => {
    try {
      await matchApi.startMatch(id);
      message.success('匹配成功');
      fetchRecords();
    } catch (error) {
      message.error('匹配失败');
      console.error(error);
    }
  };

  const handleViewMatch = (id) => {
    navigate(`/match/edit/${id}`);
  };

  const handleCreateMatch = () => {
    navigate(`/match/create?type=${activeTab}`);
  };

  const renderMatchStatus = (status) => {
    if (status === '未匹配') {
      return <Tag icon={<SyncOutlined spin />} color="processing">未匹配</Tag>;
    } else if (status === '匹配成功') {
      return <Tag icon={<CheckCircleOutlined />} color="success">匹配成功</Tag>;
    } else {
      return <Tag icon={<CloseCircleOutlined />} color="error">匹配失败</Tag>;
    }
  };

  const separateColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id' },
    { title: '科目', dataIndex: 'subject', key: 'subject' },
    { title: '题目文件名', dataIndex: 'question_filename', key: 'question_filename' },
    { title: '答案文件名', dataIndex: 'answer_filename', key: 'answer_filename' },
    { title: '上传人', dataIndex: 'uploader_name', key: 'uploader_name' },
    { 
      title: '上传时间', 
      dataIndex: 'upload_time', 
      key: 'upload_time',
      render: (text) => new Date(text).toLocaleString()
    },
    { title: '消耗Tokens数', dataIndex: 'token_usage', key: 'token_usage' },
    { 
      title: '匹配状态', 
      dataIndex: 'match_status', 
      key: 'match_status',
      render: renderMatchStatus
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button 
            type="primary" 
            onClick={() => handleViewMatch(record.id)}
          >
            查看
          </Button>
          <Popconfirm
            title="确定要删除这条记录吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const integratedColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id' },
    { title: '科目', dataIndex: 'subject', key: 'subject' },
    { title: '题目答案文件名', dataIndex: 'question_filename', key: 'question_filename' },
    { title: '上传人', dataIndex: 'uploader_name', key: 'uploader_name' },
    { 
      title: '上传时间', 
      dataIndex: 'upload_time', 
      key: 'upload_time',
      render: (text) => new Date(text).toLocaleString()
    },
    { title: '消耗Tokens数', dataIndex: 'token_usage', key: 'token_usage' },
    { 
      title: '匹配状态', 
      dataIndex: 'match_status', 
      key: 'match_status',
      render: renderMatchStatus
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button 
            type="primary" 
            onClick={() => handleViewMatch(record.id)}
          >
            查看
          </Button>
          <Popconfirm
            title="确定要删除这条记录吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const items = [
    {
      key: '题答分离',
      label: '题答分离匹配',
      children: (
        <Table 
          columns={separateColumns} 
          dataSource={records} 
          rowKey="id" 
          loading={loading}
        />
      ),
    },
    {
      key: '题答一体',
      label: '题答一体匹配',
      children: (
        <Table 
          columns={integratedColumns} 
          dataSource={records} 
          rowKey="id" 
          loading={loading}
        />
      ),
    },
  ];

  return (
    <div className="match-list-container">
      <Card 
        title="题答匹配管理" 
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleCreateMatch}
          >
            新建匹配
          </Button>
        }
      >
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={items}
        />
      </Card>
    </div>
  );
};

export default MatchList; 